<template> 
  <TsSection append-class="mb-0 ">
    <div>
      <TsBreadcrumb v-if="algoliaStore.breadcrumb_data.length > 0" root-class="pt-2  lg:my-4"
                    :data="algoliaStore.breadcrumb_data" />
    </div>
  </TsSection>

  <div v-if="searchResultStore.searchResultState.status === 'success'">
    <TsSearchResults
      search-type="category-search"
      :header-content="
        menuStore.category && menuStore.category.taxonomy
          ? menuStore.category.taxonomy.name
          : ''
      "
      :bannerData="algoliaStore.bannerData"
    >
      <template #category-header>
        <div>
          <div v-if="displayedDescription" class="mb-6 [&_a]:text-[#5f91c3] [&_a]:no-underline [&_a]:hover:underline [&_a]:cursor-pointer">
            <div
              :class="{
                'overflow-hidden': !isDescriptionExpanded,
                block: isDescriptionExpanded,
                'line-clamp-2': isMobile && !isDescriptionExpanded,
              }"
            >
              <div v-html="displayedDescription"></div>
            </div>
            <span
              class="text-info cursor-pointer underline mt-1"
              v-if="shouldShowViewMore"
              data-testid="view-more-less-category-description"
              @click="toggleDescriptionView"
            >
              {{
                isDescriptionExpanded
                  ? useTranslation("readLess", "lees Minder")
                  : useTranslation("readMore", "Lees Meer")
              }}
            </span>
          </div>
          <TsSection append-class="mb-0 px-0" container-class="px-0">
            <TsDivider append-class="my-2" />
            <div
              v-if="menuStore.category && Object.entries(menuStore.category).length > 0"
              class="category"
            >
              <TsTabsCategorySelection
                append-class=" *:text-primary *:border-0 *:font-semibold"
                :tabs="categoryTabs(menuStore.category.children)"
                :set-active-tab="false"
                @tab-click="
                  (tab, index) =>
                    handleCategoryChipClick(
                      menuStore.category.ancestors?.[0].taxonomy.name,
                      tab.label,
                      tab.id
                    )
                "
              />
            </div>
          </TsSection>
        </div>
      </template>
    </TsSearchResults>
  </div>
  <div v-if="searchResultStore.searchResultState.status === 'failed'">
    <TsNoResultFound :meta-title="getDefaultMetaTitle()"></TsNoResultFound>
  </div>
  <div v-if="searchResultStore.searchResultState.status === 'loading'">
    <TsSearchPageLoading :search-query="getCategoryNameFromRoute()"></TsSearchPageLoading>
  </div>
  <div v-if="searchResultStore.searchResultState.status === 'idle'">
    <TsSearchPageLoading :search-query="getCategoryNameFromRoute()"></TsSearchPageLoading>
  </div>

  <TsSection
    container-class="px-0"
    id="pdp-related-products"
    v-if="monetateStore.plp_recommendaions.ids?.length" 
  >
    <TsTypography as="h2" variant="h1" append-class="mb-2">
      {{ useTranslation("youMayAlsoLike", "Misschien vind je het ook leuk") }}
    </TsTypography>
    <div class="lg:-mx-1.5">
      <TsCarouselCardProduct
        @product-click="
          monetateStore.trackRecommendationClick(monetateStore.plp_recommendaions, $event)
        "
        data-testid="product-others"
        productContextType="recommended"
        :productIds="monetateStore.plp_recommendaions.ids"
    
      />
    </div>
  </TsSection>
</template>

<script setup lang="ts">
import { useTranslation } from "~/composables/useTranslation";
import { BuilderService } from "~/services/builder.service";
definePageMeta({
  path: "/:slug/:childSlug/c:id(\\d+)",
});

const TsSearchResults = defineAsyncComponent(
  () => import("~/components/organisms/TsSearchResults.vue")
);
const TsTabsCategorySelection = defineAsyncComponent(
  () => import("~/components/molecules/TsTabsCategorySelection.vue")
);
const TsNoResultFound = defineAsyncComponent(
  () => import("~/components/organisms/TsNoResultFound.vue")
);
const TsSearchPageLoading = defineAsyncComponent(
  () => import("~/components/organisms/TsSearchPageLoading.vue")
);
const TsCarouselCardProduct = defineAsyncComponent(
  () => import("~/components/organisms/TsCarouselCardProduct.vue")
);
const TsBreadcrumb = defineAsyncComponent(
  () => import("~/components/molecules/TsBreadcrumb.vue")
);
const nuxtApp = useNuxtApp();
const config = useRuntimeConfig();
const { locale } = useI18n();
const localePath = useLocalePath();
const searchResultStore = useSearchResultStore();
const algoliaStore = useAlgoliaStore();
const monetateStore = useMonetateStore();
const menuStore = useMenuStore();
const route = useRoute("slug-childSlug-cid___nl-NL___default");
const isDescriptionExpanded = ref(false);
const { isMobile, isDesktop } = useDevice();
// searchResultStore.productState.status = "loading";
const { builderApiKey } = useRuntimeConfig().public
const categoryId = route.params.id
const productListingPageFor = useTranslation('ProductListingPageFor', 'Productvermeldingspagina voor'); 
onMounted(async () => {

  const productsArray = Object.values(algoliaStore.productIDs).map(
    (productId) => ({
      productId: productId,
    })
  );

  window.monetateQ = window.monetateQ || [];
  window.monetateQ.push(["addProducts", productsArray]);
  window.monetateQ.push(["setPageType", "taxonomy"]);
  window.monetateQ.push(["trackData"]);

});

const isCategoryViewEventLogged = ref(false);

const debouncedCategoryView = useDebounceFn(() => logViewCategory(), 600, {
    maxWait: 3000
});


/**
 * @description generates a fall back meta title by crawling up the ancestor tree
 * @refer https://www.gke.pre-prod.nl.toolstation.dev/elektra/cee-stekkers/c1273
 */
function getDefaultMetaTitle() {
  if (!menuStore.category || Object.keys(menuStore.category).length === 0) {
    if(!import.meta.client){
      return 'Zoeken';
    }
    return useCapitalize(useTranslation("toSearch", "Zoeken"));
  }
  const childCategoryName = menuStore.category.taxonomy.name;
  const parentCategory = menuStore.category.ancestors.find(
    (ancestor) => ancestor.parent_id === 0
  );
  const parentCategoryName = parentCategory?.taxonomy?.name;
  if (childCategoryName && parentCategoryName)
    return `${childCategoryName} | ${parentCategoryName || ""} | Toolstation`;
  return useCapitalize(useTranslation("toSearch", "Zoeken"));
}

const toggleDescriptionView = () => {
  isDescriptionExpanded.value = !isDescriptionExpanded.value;

  logEvent({
    options: {
      eventAction: "click",
      eventProperties: {
        category: menuStore.category?.taxonomy?.name,
        action: !isDescriptionExpanded.value ? "Read Less" : "Read More",
      },
      eventInteraction: true,
      eventLabel: `plp_category_description_read_more`,
      eventName: "plp_category_description_read_more",
    },
  });
};

const shouldShowViewMore = computed(() => {
  if (
    menuStore.category &&
    menuStore.category.taxonomy &&
    menuStore.category.taxonomy.description
  ) {
    const description = menuStore.category.taxonomy.description;
    const paragraphs = description.split(/(?:\r\n|\n|<br>)+/);
    return paragraphs.length > 1;
  }
  return false;
});

const displayedDescription = computed(() => {
  if (
    menuStore.category &&
    menuStore.category.taxonomy &&
    menuStore.category.taxonomy.description
  ) {
    const description = menuStore.category.taxonomy.description.replaceAll(
      "https://www.toolstation.nl",
      ""
    );
    return isDescriptionExpanded.value
      ? description.replace(/\r?\n/g, '<span class="block h-[5px]"></span>')
      : description
          .split(/\r?\n/)[0]
          .replace(/\r?\n/g, '<span class="block h-[5px]"></span>');
  }
  return "";
});

const defaultDescriptionPreProdNL = computed(() => {
  if (!menuStore.category || !menuStore.category.taxonomy) {
    return "";
  }
  const taxonomyName = useCapitalize(menuStore.category.taxonomy.name).replaceAll(
    "-",
    " "
  );

  return `Ontdek ons assortiment ${taxonomyName} voor lage prijzen. 
    Ben je geïnteresseerd in het kopen van ${taxonomyName}? 
    Neem dan een kijkje in ons scherp geprijsde assortiment. 
    Wil je snel aan de slag met jouw aankoop? 
    Kom langs in één van onze vele vestigingen. 
    Je kunt de voorraad van al onze producten in de verschillende vestigingen online checken. 
    Plaats je jouw bestelling online met Click & Collect? 
    Jouw bestelling ligt dan 10 minuter later in de door jou gekozen vestiging voor je klaar. 
    Ook voor het online bestellen ben je bij Toolstation aan het juiste adres. 
    Bij bestellingen vanaf €20,- betaal je geen verzendkosten en 
    als je voor 22.00 besteld, heb je de bestelling de volgende dag al in huis`;
});

async function onLoad() {
  let options = {
    category_name: getCategoryNameFromRoute(),
    category_page_id: getCategoryPageIdFromRoute(),
    bake_category: bakeCategoryAsFilter(),
  };

  await useAsyncData("onLoadPlp", () =>
    searchResultStore.onLoadPlp(route, "category-search", options, locale.value,isMobile,isDesktop,config,nuxtApp)
  );
}
async function fetchStockOfProduct() {
  await useAsyncData("fetchStockOfProduct", () =>
      searchResultStore.fetchStockOfProduct()
  );
}

async function getBrandData() {
  const builderObject = new BuilderService(builderApiKey)
  algoliaStore.bannerData  = await builderObject.getItem('products-listing', { urlPath: route.path }, {},
  {'data.categories.$regex': categoryId})
}

// async function getRecommendations() {
//   await useAsyncData("recommendations", () => monetateStore.trackPlpView());
// }

// Create a computed property that combines the query and the trigger
const watchedQuery = computed(() => ({
  query: route.query,
  trigger: searchResultStore.isWatchTrigger,
}));

const stopWatcher = watch(watchedQuery, async (newQuery, oldQuery) => {
  // Check if only the layout has changed
  const layoutChanged = oldQuery.layout !== newQuery.layout;

  // Check if any other property has changed
  const otherQueriesChanged = Object.keys(newQuery).some(
    (key) => key !== "layout" && newQuery[key] !== oldQuery[key]
  );

  // If only the layout has changed, return
  if (layoutChanged && !otherQueriesChanged) {
    return;
  }

  await menuStore.getCategoryById(route.params.id);

  await algoliaStore.createSubCategoryBreadCrumb(menuStore.category);

  let options = {
    category_name: getCategoryNameFromRoute(),
    category_page_id: getCategoryPageIdFromRoute(),
    bake_category: bakeCategoryAsFilter(),
  };

  await searchResultStore.onLoadPlp(route, "category-search", options,locale.value,isMobile,isDesktop,config,nuxtApp);
  await searchResultStore.fetchStockOfProduct()
});

onUnmounted(() => {
  stopWatcher();
});

const { data: category, pending, error } = await useLazyAsyncData("category-"+route.params.id, async () =>{
  const res = await getCategoryTabs(route.params.id)
  if(!res || (res.data && res.data.error)){
    let code = res && res.errors && !res.errors.includes('404 Not Found') ? '500' : res && res.data && res.data.error && res.data.error.code ? res.data.error.code : import.meta.server ? 404 : 500;
    let message = res && res.errors && !res.errors.includes('404 Not Found') ? res.errors : res && res.data && res.data.error && res.data.error.message ? res.data.error.message : import.meta.server ? 'Category not found in server' : 'Something went wrong';
    showError({
      statusCode: code,
      statusMessage: message,
      data:res
    })
  }

  return res;
});

if(error.value){
  throw createError(error.value);
}


await useAsyncData(`category-plp-brand-${route.params.id}`, async () =>{

  const builderObject = new BuilderService(builderApiKey)
  algoliaStore.bannerData  = await builderObject.getItem('products-listing', { urlPath: route.path }, {},
      {'data.categories.$regex': categoryId},nuxtApp)
  return true;
},{
  lazy:true,
  watch:[category]
})

const { data: productList } = await useAsyncData(`category-search-${route.params.id}`, async () =>{
  let options = {
    category_name: getCategoryNameFromRoute(),
    category_page_id: getCategoryPageIdFromRoute(),
    bake_category: bakeCategoryAsFilter(),
  };
  return searchResultStore.onLoadPlp(route,"category-search",options,locale.value,isMobile,isDesktop,config,nuxtApp) 
})

const { data: stock } = await useAsyncData(`category-plp-stock-${route.params.id}`,
    async () => {
      if(productList.value){
        return searchResultStore.fetchStockOfProduct(nuxtApp,config)
      }
      return false;
    },{
      lazy:true,
      watch:[productList]
})

watchEffect(() => {
  useHead({
    title: () => menuStore.category && menuStore.category.taxonomy ? extractCategoryMetaTitle(menuStore.category.taxonomy) : getDefaultMetaTitle(),
    meta: [
      {
        name: "description",
        content:
          menuStore.category && menuStore.category.taxonomy ? extractCategoryMetaDescription(menuStore.category.taxonomy) :
           `${productListingPageFor} ${getCategoryNameFromRoute()}`,
      },
      {
        name: "title",
        content: menuStore.category && menuStore.category.taxonomy ? extractCategoryMetaTitle(menuStore.category.taxonomy) : getDefaultMetaTitle(),
      },
    ],
  });

  if(isCategoryViewEventLogged.value) return;
  const isTaxonomyDataValid = Object.keys(menuStore.category).length > 0 && route.params.id === String(menuStore.category?.taxonomy?.id);
  if(isTaxonomyDataValid && searchResultStore.products && searchResultStore.products.length > 0) {
    debouncedCategoryView();
    isCategoryViewEventLogged.value = true;
  }
});

// await useAsyncData("category-"+route.params.id, () => getCategoryTabs(route.params.id), {
//   lazy : true
// });

// await onLoad();
// await fetchStockOfProduct();

const categoryTabs = (data: any): string[] => {
  const taxonomyData: any[] = [];

  // Iterate over each item in the response array
  data.forEach((item: any) => {
    // Extract the taxonomy id and name and create a new object with `id` and `label`
    taxonomyData.push({
      id: item.taxonomy.id,
      label: item.taxonomy.name,
    });
  });

  return taxonomyData;
};

function bakeCategoryAsFilter() {
  return "categoryPageId:" + getSearchQueryFromRoute();
}

function getSearchQueryFromRoute() {
  // const match = route.params?.subcategoryid.match(/(\d+)$/); // Regular expression to match digits at the end of the string
  // return match ? match[0] : null;
  const subcategoryId = route.params.id;
  return subcategoryId ? subcategoryId : null;
}

function getCategoryPageIdFromRoute() {
  // const match = route.params?.subcategoryid.match(/(\d+)$/); // Regular expression to match digits at the end of the string
  // return match ? match[0] : null;
  return route.params.id ? route.params.id : null;
}

function getCategoryNameFromRoute() {
  // const match = combineSubcategoryStrings().match(/^(.*?)-?\d+$/); // Regular expression to match everything before the last number
  // return match[1];
  return route.params.childSlug;
}

async function getCategoryTabs(id) {
  const response = await menuStore.getCategoryById(id,nuxtApp,config);

  if (menuStore.category && menuStore.category.taxonomy) {
    await createSubCategoryBreadCrumb();
  }
  return response;
}

async function createSubCategoryBreadCrumb() {
  algoliaStore.createSubCategoryBreadCrumb(menuStore.category);
}

const handleCategoryChipClick = async (
  categoryName: string,
  subCategoryName: string,
  subCategoryId: number
) => {
  await navigateTo(
    localePath(getSubCategorySlug(categoryName, subCategoryName, subCategoryId))
  );
};
/*
function checkRouteValidations() {
  // Extract route parameters safely
  const categoryName = route.params?.slug || "";
  const subCategoryName = route.params?.childSlug || "";

  // Ensure menuStore.category exists before accessing properties
  if (!menuStore?.category || Object.keys(menuStore.category).length === 0) {
    return false;
  }

  // Extract category and subcategory names safely
  const confirmCategoryName =
    menuStore?.category?.ancestors?.[0]?.taxonomy?.name || "";
  const confirmSubCategoryName = menuStore?.category?.taxonomy?.name || "";

  let updated = false;
  const updatedParams = { ...route.params };

  // Ensure 'slug' is always present in updatedParams
  if (
    categoryName &&
    typeof categoryName === "string" &&
    normalizeString(categoryName) !== normalizeString(confirmCategoryName)
  ) {
    updatedParams.slug = formatForUrl(confirmCategoryName);
    updated = true;
  }

  // Ensure 'childSlug' is always present in updatedParams if it exists
  if (
    subCategoryName &&
    typeof subCategoryName === "string" &&
    normalizeString(subCategoryName) !== normalizeString(confirmSubCategoryName)
  ) {
    updatedParams.childSlug = formatForUrl(confirmSubCategoryName);
    updated = true;
  }

  // Prevent route update if required param 'slug' is missing
  if (updated && updatedParams.slug) {
    router.replace({
      name: route.name,
      params: updatedParams,
      query: route.query,
    });
  }
}
*/

const normalizeString = (str: string) => {
  return toRoutingSlug(str);
};

const formatForUrl = (str: string) => {
  return toRoutingSlug(str);
};
function logViewCategory() {
  if (!menuStore.category || !Array.isArray(menuStore.category.ancestors)) {
    return;
  }
  const categoryTaxonomy = menuStore.category?.ancestors[0]?.taxonomy?.name;
  const categoryTaxonomy2 = menuStore.category?.ancestors[1]?.taxonomy?.name;

  // checkRouteValidations();
  import("~/services/exponeaEvents.service").then(({ trackExponeaEvent }) => {
    const eventParams = {
      category_title: route.params.childSlug,
      category_level1: categoryTaxonomy,
      category_level2: categoryTaxonomy2,
      category_level3: route.params.childSlug,
      language: locale.value,
      path: route.path,
      product_list: searchResultStore.products?.map((product) => product.code),
    };
    trackExponeaEvent("view_category", eventParams);
  });
}
</script>

